import React, { useState, useEffect } from 'react';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 pt-28 pb-8 md:pb-12 lg:pt-32 lg:pb-14"
      /* Removed enforced viewport height to eliminate large bottom gap on shorter content. */
    >
      {/* Enhanced Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-20 right-20 w-80 h-80 bg-purple-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-cyan-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-indigo-500/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '3s' }}></div>
      </div>

      {/* Modern Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}></div>

  <div className="container relative z-10">
    <div className="grid lg:grid-cols-5 gap-12 items-center">
          {/* Left Content - 3 columns */}
          <div className="lg:col-span-3 text-white space-y-8 animate-on-scroll">
            {/* Removed professional badge and added spacing */}
            <div className="pt-6"></div>

            <div className="space-y-6">
              <h1 className="text-4xl lg:text-7xl font-black leading-tight">
                <span className="block text-white">Trusted</span>
                <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 text-transparent bg-clip-text">
                  Verification &
                </span>
                <span className="block text-white">Due Diligence</span>
                <span className="block bg-gradient-to-r from-yellow-400 to-orange-400 text-transparent bg-clip-text">
                  Services
                </span>
              </h1>

              <div className="flex items-center space-x-2">
                <div className="w-1 h-12 bg-gradient-to-b from-cyan-400 to-blue-600 rounded-full"></div>
                <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
                  Across South India
                </p>
              </div>
            </div>

            <p className="text-lg lg:text-xl text-blue-200 leading-relaxed max-w-2xl">
              <strong className="text-white">Timely, Confidential & Professional</strong> verification solutions. 
              Delivering accurate reports with the highest degree of confidentiality across 
              <span className="text-cyan-300 font-semibold"> Andhra Pradesh, Telangana, Karnataka & Tamil Nadu.</span>
            </p>

            {/* Rotating Tagline Carousel */}
            <CarouselTaglines />

            <div className="flex flex-col sm:flex-row gap-6 pt-2 md:pt-4">
              <button 
                onClick={() => scrollToSection('contact')}
                className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-cyan-500/25"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative flex items-center justify-center">
                  <i className="fas fa-phone mr-3"></i>
                  Get Professional Consultation
                  <i className="fas fa-arrow-right ml-3 group-hover:translate-x-1 transition-transform"></i>
                </span>
              </button>

              <button 
                onClick={() => scrollToSection('services')}
                className="group relative border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-bold text-lg backdrop-blur-sm hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
              >
                <span className="flex items-center justify-center">
                  <i className="fas fa-shield-alt mr-3"></i>
                  Explore Services
                  <i className="fas fa-chevron-right ml-3 group-hover:translate-x-1 transition-transform"></i>
                </span>
              </button>
            </div>

            {/* Enhanced Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-6 pb-0 md:pb-2">
              <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                <div className="text-2xl lg:text-3xl font-bold text-cyan-400 mb-1">2019</div>
                <div className="text-blue-200 text-xs lg:text-sm">Established</div>
              </div>
              <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                <div className="text-2xl lg:text-3xl font-bold text-green-400 mb-1">4+</div>
                <div className="text-blue-200 text-xs lg:text-sm">States Coverage</div>
              </div>
              <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                <div className="text-2xl lg:text-3xl font-bold text-yellow-400 mb-1">100%</div>
                <div className="text-blue-200 text-xs lg:text-sm">Confidential</div>
              </div>
            </div>
          </div>

          {/* Right Image Section - 2 columns with small attractive image */}
          <div className="lg:col-span-2 relative animate-on-scroll">
            <div className="relative z-10 max-w-md mx-auto">
              {/* Main Professional Image - Smaller and More Attractive */}
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/30 to-purple-500/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="relative bg-white/10 backdrop-blur-sm p-6 rounded-3xl border border-white/20">
                  <img 
                    src="https://images.unsplash.com/photo-1556157382-97eda2d62296?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" 
                    alt="Professional Detective Services" 
                    className="w-full h-auto rounded-2xl shadow-2xl group-hover:shadow-cyan-500/25 transition-all duration-500"
                  />

                  {/* Floating Professional Cards - Enhanced Design */}
                  <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white p-3 rounded-2xl shadow-2xl animate-bounce backdrop-blur-sm" style={{ animationDelay: '0.5s' }}>
                    <div className="text-center">
                      <i className="fas fa-certificate text-lg mb-1"></i>
                      <div className="font-bold text-sm">Verified</div>
                      <div className="text-xs opacity-90">Partner</div>
                    </div>
                  </div>

                  <div className="absolute -bottom-3 -left-3 bg-white text-gray-800 p-3 rounded-2xl shadow-2xl animate-bounce backdrop-blur-sm" style={{ animationDelay: '1s' }}>
                    <div className="text-center">
                      <i className="fas fa-map-marked-alt text-lg mb-1 text-blue-600"></i>
                      <div className="font-bold text-sm">25+</div>
                      <div className="text-xs text-gray-600">Cities</div>
                    </div>
                  </div>

                  <div className="absolute top-1/2 -left-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2 rounded-xl shadow-2xl animate-bounce" style={{ animationDelay: '1.5s' }}>
                    <div className="text-center">
                      <i className="fas fa-shield-alt text-sm mb-1"></i>
                      <div className="font-bold text-xs">100%</div>
                      <div className="text-xs opacity-90">Secure</div>
                    </div>
                  </div>

                  {/* New Attractive Floating Element */}
                  <div className="absolute top-20 -right-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-3 rounded-2xl shadow-2xl animate-bounce" style={{ animationDelay: '2s' }}>
                    <div className="text-center">
                      <i className="fas fa-clock text-lg mb-1"></i>
                      <div className="font-bold text-sm">24/7</div>
                      <div className="text-xs opacity-90">Available</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Decorative Elements */}
              <div className="absolute -inset-8 bg-gradient-to-r from-cyan-500/5 to-purple-500/5 rounded-3xl -z-10 blur-2xl"></div>
              <div className="absolute top-10 right-10 w-20 h-20 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl -z-10 animate-pulse"></div>
              <div className="absolute bottom-10 left-10 w-16 h-16 bg-gradient-to-r from-cyan-400/20 to-blue-400/20 rounded-full blur-xl -z-10 animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
            {/* Scroll Indicator placed directly beneath image */}
            <div className="mt-8 flex justify-center animate-bounce">
              <button
                onClick={() => scrollToSection('about')}
                className="group text-white/70 hover:text-white transition-all duration-300"
                aria-label="Scroll to About section"
              >
                <div className="flex flex-col items-center space-y-2">
                  <span className="text-xs md:text-sm font-medium tracking-wide">Discover Our Excellence</span>
                  <div className="w-7 h-10 md:w-8 md:h-12 border-2 border-white/30 rounded-full flex justify-center">
                    <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

  // Simple text carousel component placed within Hero
  const taglines = [
    'Trusted Verification & Due Diligence Services Across South India',
    'Best Verification Service Provider across South India.',
    'Result-Driven Due Diligence & Verification Services.'
  ];

  function CarouselTaglines() {
    const [index, setIndex] = useState(0);

    useEffect(() => {
      const interval = setInterval(() => {
        setIndex((prev) => (prev + 1) % taglines.length);
      }, 3500); // 3.5s per slide
      return () => clearInterval(interval);
    }, []);

    return (
      <div className="h-10 mt-4 relative select-none" role="status" aria-live="polite">
        {taglines.map((text, i) => {
          const isActive = i === index;
          return (
            <span
              key={i}
              className={`absolute left-0 top-0 w-full transition-all duration-700 ease-out text-base lg:text-lg font-semibold tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-teal-200 to-blue-200 ${
                isActive ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'
              }`}
            >
              {text}
            </span>
          );
        })}
      </div>
    );
  }

export default Hero;